import React, { useState } from 'react';
import { Toaster } from './components/ui/sonner';
import { LoadingSpinner } from './components/LoadingSpinner';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { SocialMedia } from './components/SocialMedia';
import { Games } from './components/Games';
import { About } from './components/About';
import { TwitchStream } from './components/TwitchStream';
import { PoppyCat } from './components/PoppyCat';
import { Bizenghast } from './components/Bizenghast';
import { Footer } from './components/Footer';
import { SupabaseLogin } from './components/SupabaseLogin';
import { InvestorDashboard } from './components/InvestorDashboard';
import { SupabaseAuthProvider, useAuth } from './components/SupabaseAuth';
import { TermsOfService } from './components/TermsOfService';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { CookiePolicy } from './components/CookiePolicy';
import { ContactForm } from './components/ContactForm';

// Simple Error Boundary
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('Error caught by boundary:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="text-red-400 text-2xl font-bold">SYSTEM_ERROR</div>
            <div className="text-gray-400">Something went wrong</div>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Views
type ViewType = 'home' | 'login' | 'dashboard' | 'terms' | 'privacy' | 'cookies' | 'contact';

function normalizePathname(pathname: string): string {
  // Normalize for direct-linking on static hosts:
  // - lower-case
  // - trim trailing slashes (except keep "/" as-is)
  const lower = (pathname || '/').toLowerCase();
  const trimmed = lower.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

function setCanonicalPath(path: string) {
  // Keep canonical aligned with the current SPA path (avoid query/hash).
  const origin = window.location.origin;
  const canonicalHref = `${origin}${path}`;

  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = canonicalHref;
}

// Helper function to get initial view from URL
function getInitialViewFromURL(): ViewType {
  const path = normalizePathname(window.location.pathname);
  switch (path) {
    case '/terms':
    case '/terms-of-service':
      return 'terms';
    case '/privacy':
    case '/privacy-policy':
      return 'privacy';
    case '/cookies':
    case '/cookie-policy':
      return 'cookies';
    case '/contact':
    case '/contact-us':
      return 'contact';
    case '/login':
      return 'login';
    case '/dashboard':
      return 'dashboard';
    default:
      return 'home';
  }
}

// Main App Content
function AppContent() {
  const initialView = React.useMemo(() => getInitialViewFromURL(), []);
  const [currentView, setCurrentView] = useState<ViewType>(initialView);
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const [postLoginView, setPostLoginView] = useState<ViewType | null>(
    initialView === 'dashboard' ? 'dashboard' : null
  );
  
  // Debug logging
  console.log('AppContent render - isLoading:', isLoading);

  // If user deep-links to dashboard while logged out, redirect to login (then return to dashboard after auth).
  React.useEffect(() => {
    if (!isAuthenticated && currentView === 'dashboard') {
      setPostLoginView('dashboard');
      setCurrentView('login');
    }
  }, [isAuthenticated, currentView]);

  // Auto-redirect after successful login
  React.useEffect(() => {
    if (isAuthenticated && currentView === 'login') {
      setCurrentView(postLoginView ?? 'dashboard');
      setPostLoginView(null);
    }
  }, [isAuthenticated, currentView, postLoginView]);

  // Handle browser back/forward navigation
  React.useEffect(() => {
    const handlePopState = () => {
      setCurrentView(getInitialViewFromURL());
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update URL when view changes
  React.useEffect(() => {
    const getPathForView = (view: ViewType): string => {
      switch (view) {
        // Use descriptive canonical URLs, but keep aliases supported in getInitialViewFromURL().
        case 'terms': return '/terms-of-service';
        case 'privacy': return '/privacy-policy';
        case 'cookies': return '/cookie-policy';
        case 'contact': return '/contact';
        case 'login': return '/login';
        case 'dashboard': return '/dashboard';
        default: return '/';
      }
    };

    const newPath = getPathForView(currentView);
    if (normalizePathname(window.location.pathname) !== newPath) {
      window.history.pushState({}, '', newPath);
    }

    // Update canonical on every view change (helps SEO + prevents "always-homepage" canonical).
    setCanonicalPath(newPath);
  }, [currentView]);

  const handleInvestorClick = () => {
    if (isAuthenticated) {
      setCurrentView('dashboard');
    } else {
      setPostLoginView('dashboard');
      setCurrentView('login');
    }
  };


  const handleBackToHome = () => {
    setCurrentView('home');
  };

  const handleLogout = () => {
    setPostLoginView(null);
    setCurrentView('home');
  };

  const handleTermsClick = () => {
    setCurrentView('terms');
  };

  const handlePrivacyClick = () => {
    setCurrentView('privacy');
  };

  const handleCookiesClick = () => {
    setCurrentView('cookies');
  };

  const handleContactClick = () => {
    setCurrentView('contact');
  };

  const handleLoadingComplete = () => {
    console.log('Loading completed, hiding spinner');
    setIsLoading(false);
  };

  if (isLoading) {
    console.log('Showing LoadingSpinner');
    return <LoadingSpinner onLoadingComplete={handleLoadingComplete} />;
  }

  if (currentView === 'login') {
    return (
      <div className="min-h-screen bg-slate-900 text-white">
        <SupabaseLogin onBack={handleBackToHome} />
        <Toaster />
      </div>
    );
  }

  if (currentView === 'dashboard' && isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 text-white">
        <InvestorDashboard 
          onHome={handleBackToHome}
          onLogout={handleLogout}
        />
        <Toaster />
      </div>
    );
  }

  if (currentView === 'terms') {
    return <TermsOfService onBack={handleBackToHome} />;
  }

  if (currentView === 'privacy') {
    return <PrivacyPolicy onBack={handleBackToHome} />;
  }

  if (currentView === 'cookies') {
    return <CookiePolicy onBack={handleBackToHome} />;
  }

  if (currentView === 'contact') {
    return <ContactForm onBack={handleBackToHome} />;
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Header 
        onInvestorClick={handleInvestorClick}
        onContactClick={handleContactClick} 
      />
      <Hero />
      <About />
      <Games />
      <SocialMedia />
      <TwitchStream />
      <PoppyCat />
      <Bizenghast />
      <Footer 
        onTermsClick={handleTermsClick}
        onPrivacyClick={handlePrivacyClick}
        onCookiesClick={handleCookiesClick}
        onContactClick={handleContactClick}
      />
      <Toaster />
    </div>
  );
}

// Main App with Error Boundary
function App() {
  return (
    <ErrorBoundary>
      <SupabaseAuthProvider>
        <AppContent />
      </SupabaseAuthProvider>
    </ErrorBoundary>
  );
}

export default App;